<template>
  <section>
    <EnExperiencesSearchSection ref="heading" :selected-search-conditions="selectedSearchConditions">
      <!-- モバイル: list/map トグル（検索バーと同一行・右。Figma 準拠）。結果があるときのみ表示 -->
      <template v-if="experiences.length > 0 && !hasErrorExperiencesSearch && !hasNoGeocodedExperiences" #action>
        <div aria-label="View mode" class="flex shrink-0 items-center gap-0.5 rounded border border-stone-300 bg-white p-0.5 md:hidden" role="tablist">
          <button
            aria-label="List view"
            role="tab"
            type="button"
            :aria-selected="viewMode === 'list'"
            :class="['flex h-9 w-9 items-center justify-center rounded transition-colors', viewMode === 'list' ? 'bg-gold3 text-white' : 'text-gray4']"
            @click="switchViewMode('list')"
          >
            <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <rect height="6" rx="1" width="16" x="4" y="5" />
              <rect height="6" rx="1" width="16" x="4" y="13" />
            </svg>
          </button>
          <button
            aria-label="Map view"
            role="tab"
            type="button"
            :aria-selected="viewMode === 'map'"
            :class="['flex h-9 w-9 items-center justify-center rounded transition-colors', viewMode === 'map' ? 'bg-gold3 text-white' : 'text-gray4']"
            @click="switchViewMode('map')"
          >
            <svg aria-hidden="true" class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z" />
            </svg>
          </button>
        </div>
      </template>
    </EnExperiencesSearchSection>
    <div v-if="isLoadingExperiencesSearch">
      <AtomsLoading class="m-auto h-full w-full" />
    </div>
    <div>
      <template v-if="selectedPageNum === 1 && !showMobileMap">
        <div class="pt-4 md:pt-8">
          <div :class="['ml-4 md:m-auto md:w-11/12 md:max-w-[1216px]', { 'mr-4': featuredList.length === 1 }]">
            <h3 class="mb-5 flex scroll-mt-20 font-Spectral text-xl tracking-wide text-gold4 md:mb-4 md:text-[26px] md:leading-[40px]">
              All Experiences
              <span class="mx-2 flex flex-grow items-center">
                <span class="h-[0.5px] flex-grow bg-gold4"></span>
              </span>
            </h3>
          </div>
        </div>
      </template>

      <div v-if="experiences.length == 0 || hasErrorExperiencesSearch">
        <div class="flex flex-col items-center justify-center pb-10 text-center md:pb-20">
          <p class="mt-10 text-lg font-normal leading-normal tracking-wide text-stone-700 md:text-xl md:leading-loose">No results found</p>
          <p class="my-4 px-2 text-base font-light leading-normal text-stone-500">Try adjusting your filters to find more experiences.</p>
          <AtomsSecondaryButton class="mb-10 w-full max-w-xs" data-testid="experiences-btn" font-mode="en" :is-transparent="true" :on-click="openSearchModal">Search</AtomsSecondaryButton>
        </div>
      </div>
      <div v-else>
        <!-- モバイルのマップ全画面時は左右余白を無くしフィルター行も画面端まで（マップと揃える）。リスト表示・Desktop は従来どおりインセット。 -->
        <div class="mt-2 max-w-6xl md:mx-auto md:mb-6 md:mt-4 md:w-11/12" :class="{ 'mx-4': !showMobileMap }">
          <!-- フィルタ行（モバイル=1行横スクロール / Desktop=右に View on Map） -->
          <div class="flex items-center gap-4">
            <div class="min-w-0 flex-1">
              <FilterControlSection
                :destination-tags-query="destinationTagsQuery"
                :experience-event-start-at-dates-query="experienceEventStartAtDatesQuery"
                :full-bleed-scroll="showMobileMap"
                :on-instant-booking-click="handleInstantBookingClick"
                :preference-tags-query="preferenceTagsQuery"
                :selected-dates="selectedDatesConditions"
                :theme-tags-query="themeTagsQuery"
                single-row-scroll
              />
            </div>
            <!-- Desktop: View on Map（地図非表示時のみ。スプリット時は地図側の × で閉じる） -->
            <button
              v-if="!mapOpen && !hasNoGeocodedExperiences"
              class="hidden h-[40px] shrink-0 items-center whitespace-nowrap rounded-sm bg-gold3 px-4 font-Spectral text-[17px] leading-6 text-white shadow-[0px_4px_48px_0px_rgba(0,0,0,0.15)] transition-opacity hover:opacity-90 md:flex"
              type="button"
              @click="openMap"
            >
              <svg aria-hidden="true" class="mr-2 h-[18px] w-[18px]" fill="none" stroke="currentColor" stroke-width="1.25" viewBox="0 0 24 24">
                <path d="M12 21s6-5.686 6-10a6 6 0 1 0-12 0c0 4.314 6 10 6 10Z" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx="12" cy="11" r="2.25" />
              </svg>
              View on Map
            </button>
          </div>
          <!-- 件数と並び順（モバイルのマップ全画面では非表示: Figma 準拠）。並び順は件数と同じ行の右端 -->
          <div v-if="!showMobileMap" class="mt-3 flex items-center justify-between gap-4 md:mt-4 md:pl-1 xl:pl-1.5">
            <p class="text-left font-['Spectral'] text-base font-light leading-tight text-stone-500">{{ displayCount }} experiences found</p>
            <ExperienceSortSelect :model-value="selectedSortType" :on-change="handleSortChange" />
          </div>
        </div>

        <!-- 標準リスト（SSR 描画=SEO。モバイル list / Desktop 地図非表示 or 座標付き体験なし時に表示）。
             Desktop/Mobile の出し分けは CSS ブレークポイントで行い、isDesktop(JS) 依存の hydration チラつきを避ける。 -->
        <div :class="[viewMode === 'list' ? 'block' : 'hidden', !mapOpen || hasNoGeocodedExperiences ? 'md:block' : 'md:hidden']">
          <div class="mx-4 mb-10 mt-2 max-w-6xl md:mx-auto md:mb-20 md:mt-4 md:w-11/12">
            <OrganismsPagesEnExperiencesListSection :experiences="experiences" :on-experience-click="handleExperienceClick" />
          </div>
          <div class="mb-12 mt-0 flex justify-center md:mb-16 md:mt-8">
            <MoleculesPagination :on-click="onClickPager" :page-count="maxPageCount" :selected-page-num="selectedPageNum" />
          </div>
        </div>

        <!-- スプリット（Desktop）／マップ全画面（Mobile）。表示切替は CSS ブレークポイント（isDesktop 非依存）。
             殻は SSR 描画し、ピン取得完了までスケルトンを見せることで「標準グリッド→スプリット」のチラつきを防ぐ。
             地図本体（Google Maps）のみ ClientOnly で、SSR ではスケルトンにフォールバックする（常設維持）。
             SP は rounded/枠/左右余白なしのフルブリード（余白・角丸は md 以上のみ）。 -->
        <div
          class="mt-2 max-w-6xl md:mx-auto md:mb-10 md:w-11/12"
          :class="[viewMode === 'map' && !hasNoGeocodedExperiences ? 'block' : 'hidden', mapOpen && !hasNoGeocodedExperiences ? 'md:block' : 'md:hidden']"
        >
          <div class="md:flex md:gap-8">
            <!-- リストペイン（Desktop スプリット時のみ）。ビューポート連動＋クライアントページネーション -->
            <div ref="mapListPaneRef" class="hidden md:block md:w-[58%]">
              <!-- ピン取得中（SSR〜client 取得完了まで）はスケルトン -->
              <div v-if="pinsLoading" class="grid grid-cols-2 gap-x-4 gap-y-8">
                <div v-for="i in 6" :key="`pin-skeleton-${i}`" class="animate-pulse">
                  <div class="mb-2 aspect-[3/2] w-full rounded-sm bg-gray-200"></div>
                  <div class="mb-1 h-4 w-3/4 rounded bg-gray-200"></div>
                  <div class="h-3 w-1/2 rounded bg-gray-200"></div>
                </div>
              </div>
              <p v-else-if="mapPins.length === 0" class="py-10 text-center font-Spectral text-base text-gray4">No experiences in this area.</p>
              <div v-else class="grid grid-cols-2 gap-x-4 gap-y-8">
                <ExperienceMapPinCard
                  v-for="(pin, i) in pagedMapPins"
                  :key="pin.id"
                  :on-click="() => handleExperienceClick(pin.slug, i + 1)"
                  :on-mouse-enter="handleCardMouseEnter"
                  :on-mouse-leave="handleCardMouseLeave"
                  :pin="pin"
                  :selected="pin.id === selectedPinId"
                />
              </div>
              <div v-if="!pinsLoading && mapListPageCount > 1" class="mt-10 flex justify-center">
                <MoleculesPagination :on-click="onClickMapListPager" :page-count="mapListPageCount" :selected-page-num="mapListPage" />
              </div>
            </div>
            <!-- マップペイン（Desktop=縦長の固定パネル / Mobile=全画面・フルブリード）。右上の × でリストへ戻る（Desktop） -->
            <div class="relative h-[calc(100dvh-210px)] w-full overflow-hidden border-[#dad6d0] md:sticky md:top-24 md:h-[calc(100vh-160px)] md:flex-1 md:rounded-lg md:border">
              <ClientOnly>
                <ExperienceMapView
                  :are-pins-loading="pinsLoading"
                  :area-marker-layer="areaMarkerLayer"
                  :highlighted-pin-id="hoveredPinId"
                  :on-area-card-click="handleAreaCardClick"
                  :on-area-layer-rendered-change="handleAreaLayerRenderedChange"
                  :on-bounds-change="handleMapBoundsChange"
                  :on-cluster-click="handleClusterClick"
                  :on-preview-cta-click="handlePreviewCtaClick"
                  :on-select-area="handleSelectArea"
                  :on-select-pin="handleSelectPin"
                  :on-viewport-search="handleViewportSearch"
                  :on-zoom-change="handleMapZoomChange"
                  :pins="mapPins"
                  :selected-area-destination="selectedAreaDestination"
                  :selected-pin-id="selectedPinId"
                />
                <template #fallback>
                  <div class="h-full w-full animate-pulse bg-gray5"></div>
                </template>
              </ClientOnly>
              <button
                aria-label="Close map"
                class="absolute right-3 top-3 z-20 hidden h-9 w-9 items-center justify-center rounded-sm bg-white text-gray4 shadow-[0px_4px_48px_0px_rgba(0,0,0,0.15)] md:flex"
                type="button"
                @click="closeMap"
              >
                <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <template v-if="selectedPageNum === 1 && !showMobileMap">
        <div class="flex flex-col gap-y-6 pb-8 pt-6 md:gap-y-12 md:pb-16 md:pt-12">
          <FeaturedSection v-if="destinationList.length > 0" id="areas" type="destination" :featured-list="destinationList" :on-featured-click="handleFeaturedClick" />
          <FeaturedSection v-if="itineraryList.length > 0" id="itinerary" type="itinerary" :featured-list="itineraryList" :on-featured-click="handleFeaturedClick" />
          <FeaturedSection v-if="themeList.length > 0" id="themes" type="theme" :featured-list="themeList" :on-featured-click="handleFeaturedClick" />
        </div>
      </template>
    </div>
    <template v-if="showRecommendedExperiences && !showMobileMap">
      <ClientOnly>
        <RecommendedExperiencesSection :experience-sections="experienceSections" />
      </ClientOnly>
    </template>
  </section>
</template>

<script setup lang="ts">
import { stringsFromQueryValue } from '@japan-culture-and-technology/calchez-utils/src/queryUtil';
import { useMediaQuery } from '@vueuse/core';

import type { SelectedSearchConditions } from '@/components/organisms/pages/en/experiences/EnExperiencesSearchSection/EnExperiencesSearchSection.vue';
import EnExperiencesSearchSection from '@/components/organisms/pages/en/experiences/EnExperiencesSearchSection/EnExperiencesSearchSection.vue';
import { useAsyncWabunkaExperiencesSearch } from '@/composables/models/wabunka/useAsyncWabunkaExperiencesSearch';
import { useAsyncWabunkaTags } from '@/composables/models/wabunka/useAsyncWabunkaTags';
import { useSafeWindow } from '@/composables/utils/useSafeWindow';
import { useScrollToElement } from '@/composables/utils/useScrollToElement';
import { convertJstDayjsFromString } from '@/utils/date';
import { isTimeOfDayValue, type TimeOfDayValue } from '@/wabunka-src/common/components/stateful/TimeOfDayPicker/types';
import { useBuildUrl } from '@/wabunka-src/common/composables';
import { calculateMergedTimeRange } from '@/wabunka-src/common/composables/general/useTimeOfDay/useTimeOfDay';
import ExperienceMapPinCard from '@/wabunka-src/common/features/Experience/components/ExperienceMapView/ExperienceMapPinCard.vue';
import ExperienceMapView from '@/wabunka-src/common/features/Experience/components/ExperienceMapView/ExperienceMapView.vue';
import ExperienceSortSelect from '@/wabunka-src/common/features/Experience/components/ExperienceSortSelect/ExperienceSortSelect.vue';
import FilterControlSection from '@/wabunka-src/common/features/Experience/components/FilterControlSection/FilterControlSection.vue';
import RecommendedExperiencesSection from '@/wabunka-src/common/features/Experience/components/RecommendedExperiences/RecommendedExperiencesSection.vue';
import { useExperienceListSort } from '@/wabunka-src/common/features/Experience/composables/useExperienceListSort';
import { useExperienceMapView } from '@/wabunka-src/common/features/Experience/composables/useExperienceMapView';
import type { ExperienceSortType } from '@/wabunka-src/common/features/Experience/utils/experienceListSort';
import { sendGAWabunkaChangeSortEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/changeSort';
import { sendGAWabunkaSearchResultHitCountEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/searchResultHitCount';
import { sendGAWabunkaSectionSlugViewEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionSlugView';
import { sendGAWabunkaSelectExperienceInListEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/selectExperienceInList';
import { sendGAWabunkaViewExperienceListEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/viewExperienceList';
import { togglePreference } from '@/wabunka-src/common/utils/togglePreference';
import { EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS } from '@/wabunka-src/constants/experienceSectionWebPagePath';
import { WABUNKA_ALL_AREA_TAG, WABUNKA_EXPERIENCE_LIST_PER_PAGE } from '@/wabunka-src/constants/wabunkaSearch';
import { useGetExperienceSectionsQuery } from '@/wabunka-src/features/Experience';
import { useFeaturedListQuery, useFilteredFeaturedListQuery, FeaturedSection } from '@/wabunka-src/features/Featured';
import { useKeywordSearchModal } from '@/wabunka-src/features/Search/composables/general/useKeywordSearchModal';

import type { Dayjs } from 'dayjs';

const route = useRoute();
const { buildUrlWithEndSlash } = useBuildUrl();
const { open } = useKeywordSearchModal();

/**
 * パス、クエリパラメータの処理
 */
// NOTE: route.params.pathをnullチェックせずに使うとmax call stack exceededエラーが発生する
const destinationTagWebPathInRoutePath = computed<string>(() => {
  const path = route.params.path;
  if (Array.isArray(path)) {
    return path.length > 0 && path[0] !== WABUNKA_ALL_AREA_TAG ? (path[0] ?? '') : '';
  }
  return path && path !== WABUNKA_ALL_AREA_TAG ? path : '';
});
const themeTagWebPathInRoutePath = computed<string>(() => {
  const path = route.params.path;
  return Array.isArray(path) && path.length > 1 ? (path[1] ?? '') : '';
});
const joinedPaths = computed<string>(() => {
  const path = route.params.path;
  if (Array.isArray(path)) return path.join('/');
  return path ?? '';
});

const experienceEventStartAtDatesQuery = computed<string>(() => queryToString(route.query?.experienceEventStartAtDates));
const destinationTagsQuery = computed<string>(() => queryToString(route.query?.destinations, destinationTagWebPathInRoutePath.value));
const themeTagsQuery = computed<string>(() => queryToString(route.query?.themes, themeTagWebPathInRoutePath.value));
const preferenceTagsQuery = computed<string>(() => queryToString(route.query?.preferences));
const selectedPageNum = computed<number>(() => Number(stringsFromQueryValue(queryToString(route.query.page))[0] || 1));
// joinedPaths は後方で宣言されるが、遷移先の組み立ては切替時にしか評価されない
const { selectedSortType, experienceListApiSort, handleSortChange: navigateWithSortType } = useExperienceListSort(() => '/s/experiences/' + joinedPaths.value);

// timeOfDayクエリパラメータを取得し、timeRangeを計算
const timeOfDayQuery = computed<TimeOfDayValue[]>(() => {
  const value = route.query?.timeOfDay;
  if (!value) return [];
  const values = stringsFromQueryValue(value);
  return values.filter(isTimeOfDayValue);
});

const timeRangeFromTimeOfDay = computed(() => {
  const selected = new Set(timeOfDayQuery.value);
  return calculateMergedTimeRange(selected);
});

const experienceEventStartTimeRangeFromQuery = computed<string>(() => timeRangeFromTimeOfDay.value?.from ?? '');
const experienceEventStartTimeRangeToQuery = computed<string>(() => timeRangeFromTimeOfDay.value?.to ?? '');

const { experienceSections } = await useGetExperienceSectionsQuery(EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS.EXPERIENCES, []);

// Preferenceのみ選択されている場合はFeaturedListを表示しない
// Destination/Themeが選択されている場合は紐づく特集一覧を表示する
const shouldHideFeaturedList = computed(() => {
  const hasNoDestinationOrTheme = !destinationTagsQuery.value && !themeTagsQuery.value;
  const hasPreferenceRelatedFilter = !!preferenceTagsQuery.value;
  return hasPreferenceRelatedFilter && hasNoDestinationOrTheme;
});

const { featuredList } = await useFeaturedListQuery();
const filteredResult = useFilteredFeaturedListQuery(featuredList, destinationTagsQuery, themeTagsQuery);

const destinationList = computed(() => (shouldHideFeaturedList.value ? [] : filteredResult.destinationList.value));
const itineraryList = computed(() => (shouldHideFeaturedList.value ? [] : filteredResult.itineraryList.value));
const themeList = computed(() => (shouldHideFeaturedList.value ? [] : filteredResult.themeList.value));

/** フィーチャードセクションのカードがクリックされた際のイベントハンドラ */
const handleFeaturedClick = (type: string, id: string) => {
  sendGAWabunkaSectionSlugViewEvent(type, id, route.name as string);
};

/**
 * タグの処理
 */
const { tagDictByWebPath } = await useAsyncWabunkaTags();
// 選択された行き先タグを取得
const selectedDestinationTags = computed<string[]>(() => {
  const queryTags = stringsFromQueryValue(destinationTagsQuery.value) as string[];
  // webPathが一致するタグ名を取得し、undefinedでないタグのみ返す
  return queryTags.map((queryTag) => tagDictByWebPath.value[queryTag]?.name).filter((tag) => tag !== undefined) as string[];
});

// 選択されたテーマタグを取得
const selectedThemeTags = computed<string[]>(() => {
  const queryTags = stringsFromQueryValue(themeTagsQuery.value) as string[];
  // webPathが一致するタグ名を取得し、undefinedでないタグのみ返す
  return queryTags.map((queryTag) => tagDictByWebPath.value[queryTag]?.name).filter((tag) => tag !== undefined) as string[];
});

// 選択されたPreferenceタグを取得
const selectedPreferenceTags = computed<string[]>(() => {
  const queryTags = stringsFromQueryValue(preferenceTagsQuery.value) as string[];
  // webPathが一致するタグ名を取得し、undefinedでないタグのみ返す
  return queryTags.map((queryTag) => tagDictByWebPath.value[queryTag]?.name).filter((tag) => tag !== undefined) as string[];
});

// timeOfDayの表示用ラベルを生成（morning -> Morning）
const selectedTimeOfDayLabels = computed<string[]>(() => {
  return timeOfDayQuery.value.map((v) => v.charAt(0).toUpperCase() + v.slice(1));
});

const selectedSearchConditions = computed<SelectedSearchConditions>(() => {
  return {
    Destinations: selectedDestinationTags.value,
    Themes: selectedThemeTags.value,
    Preferences: selectedPreferenceTags.value,
    TimeOfDay: selectedTimeOfDayLabels.value,
  };
});

const selectedDatesConditions = computed<Dayjs[]>(() => {
  const dates: Dayjs[] = [];
  const experienceEventStartAtDates = experienceEventStartAtDatesQuery.value.split(',');
  if (experienceEventStartAtDatesQuery.value !== '') {
    for (const date of experienceEventStartAtDates) {
      dates.push(convertJstDayjsFromString(date));
    }
  }
  // 日付順でソートする
  dates.sort((a, b) => a.diff(b));
  return dates;
});

const {
  experiences,
  meta,
  totalCount,
  hasError: hasErrorExperiencesSearch,
  hasNoResponse: hasNoResponseExperiencesSearch,
  isLoading: isLoadingExperiencesSearch,
} = await useAsyncWabunkaExperiencesSearch({
  experienceEventStartAtDates: experienceEventStartAtDatesQuery,
  destinations: destinationTagsQuery,
  themes: themeTagsQuery,
  preferences: preferenceTagsQuery,
  slugs: ref([]),
  page: selectedPageNum,
  perPage: ref(WABUNKA_EXPERIENCE_LIST_PER_PAGE),
  experienceEventStartTimeRangeFrom: experienceEventStartTimeRangeFromQuery,
  experienceEventStartTimeRangeTo: experienceEventStartTimeRangeToQuery,
  sort: experienceListApiSort,
});

const maxPageCount = computed<number>(() => {
  return Math.ceil(totalCount.value / WABUNKA_EXPERIENCE_LIST_PER_PAGE);
});

/**
 * 一覧マップビュー（JCAT-5406 / Design Doc §UC-1・確定事項）
 *
 * - Desktop は「All Experiences（検索結果本体）」をスプリット（左リスト＋右マップ）で既定表示（F-04）。
 *   × でマップを閉じると標準リスト（3カラム）に戻る。Featured/Recommended は上下にフルワイドで存置。
 * - Mobile は list/map を全画面トグル（URL クエリ `view=map` で保持。切替後もフィルタ=他クエリを維持）。
 * - マップは Option A（永続インスタンス）で常設し、v-show で表示制御して切替時に再生成しない。
 * - マップは座標を持つ体験をピン表示し、地図の描画範囲（bbox）で追従。左リストはビューポート連動＋
 *   クライアントページネーション（ビューポート変更で1ページ目にリセット）。ピン⇔カードは双方向同期（F-14/F-15）。
 */
const viewMode = computed<'list' | 'map'>(() => (queryToString(route.query.view) === 'map' ? 'map' : 'list'));

// Desktop 判定（>=768px = Tailwind md）。SSR/初期は false（=標準リストを SSR 描画し SEO を担保）。client hydration 後に確定。
const isDesktop = useMediaQuery('(min-width: 768px)');

// マップ⇔リスト連動の状態・ハンドラは各ランディングと共通の useExperienceMapView に集約する
const {
  mapPins,
  mapOpen,
  hoveredPinId,
  selectedPinId,
  mapListPage,
  mapListPageCount,
  mapListPaneRef,
  pagedMapPins,
  pinsLoading,
  hasNoGeocodedExperiences,
  isMapActive,
  openMap,
  closeMap,
  handleMapBoundsChange,
  handleSelectPin,
  handleClusterClick,
  handlePreviewCtaClick,
  handleViewportSearch,
  handleCardMouseEnter,
  handleCardMouseLeave,
  onClickMapListPager,
  // エリアラベル層（広域ズーム / JCAT-5434）
  areaMarkerLayer,
  selectedAreaDestination,
  handleMapZoomChange,
  handleAreaLayerRenderedChange,
  handleSelectArea,
  handleAreaCardClick,
} = await useExperienceMapView(
  {
    experienceEventStartAtDates: experienceEventStartAtDatesQuery,
    destinations: destinationTagsQuery,
    themes: themeTagsQuery,
    preferences: preferenceTagsQuery,
    experienceEventStartTimeRangeFrom: experienceEventStartTimeRangeFromQuery,
    experienceEventStartTimeRangeTo: experienceEventStartTimeRangeToQuery,
    sort: experienceListApiSort,
  },
  { isDesktop, sourcePage: 'search_results', viewMode },
);

// 標準リスト・スプリット・マップ全画面の Desktop/Mobile 出し分けは、hydration チラつきを避けるため
// テンプレート側で CSS ブレークポイント（md:）＋ mapOpen/viewMode/hasNoGeocodedExperiences で制御する。
// ここでは、モバイルのマップ全画面時に他セクション（件数・Featured・レコメンド）を隠すための判定のみ保持する。
const showMobileMap = computed<boolean>(() => !isDesktop.value && viewMode.value === 'map' && !hasNoGeocodedExperiences.value);

// マップ表示中は総件数（totalCount）が地図の絞り込みと不一致になり誤情報になるため、
// ビューポート内のピン件数で動的表示する（取得中は総件数を見せてチラつかせない: レビュー#3）。
const displayCount = computed<number>(() => (isMapActive.value && !pinsLoading.value ? mapPins.value.length : totalCount.value));

const switchViewMode = (mode: 'list' | 'map') => {
  const query = { ...route.query };
  if (mode === 'map') {
    query.view = 'map';
  } else {
    delete query.view;
  }
  navigateTo({ path: '/s/experiences/' + joinedPaths.value, query });
};

// お勧めExperiencesを表示するかどうか
// 1. paginationの最終ページを表示している場合
// 2. 検索結果が１ページの場合
const showRecommendedExperiences = computed<boolean>(() => {
  return maxPageCount.value === 1 || totalCount.value === 0 || selectedPageNum.value === maxPageCount.value;
});

const heading = ref<HTMLElement | null>(null);
const { window } = useSafeWindow();
const { scrollTo } = useScrollToElement({ target: heading, window, offsetY: -100 });

const onClickPager = (index: number) => {
  const query = { ...route.query, page: index.toString() };
  navigateTo({ path: '/s/experiences/' + joinedPaths.value, query });
  scrollTo();
};

const handleSortChange = (sortType: ExperienceSortType) => {
  sendGAWabunkaChangeSortEvent(sortType);
  navigateWithSortType(sortType);
  scrollTo();
};

// 一覧カードのクリック。NuxtLink の遷移はカード側が持つため、ここでは計測だけを行う
const handleExperienceClick = (experienceSlug: string, position: number) => {
  sendGAWabunkaSelectExperienceInListEvent({ sortType: selectedSortType.value, clickedSlug: experienceSlug, position });
};

const INSTANT_BOOKING_WEB_PATH = 'instant-booking';

const handleInstantBookingClick = () => {
  // route.query.preferences が string[] の場合に備えて正規化する
  const currentPreferences = stringsFromQueryValue(queryToString(route.query.preferences)).join(',');
  const newPreferences = togglePreference(currentPreferences, INSTANT_BOOKING_WEB_PATH);
  const query: Record<string, string> = { ...route.query, page: '1' };
  if (newPreferences) {
    query.preferences = newPreferences;
  } else {
    delete query.preferences;
  }
  navigateTo({ path: '/s/experiences/' + joinedPaths.value, query });
  scrollTo();
};

const openSearchModal = () => {
  open();
};

onMounted(() => {
  // GAイベント送信
  sendGAWabunkaSearchResultHitCountEvent(totalCount.value, route.fullPath);
});

// view_experience_list（一覧→詳細遷移率の分母）。取得中・エラー・null 応答は null にして、確定した表示ごとに1回だけ送る。
// onMounted では絞り込み・ページ送り・ソート切替の再取得を拾えない（同じページコンポーネントが使い回されるため）
// route.fullPath は鍵に使わない。?view=map のような一覧の取得と無関係なクエリでも分母が増えてしまう
const settledExperienceListKey = computed<string | null>(() => {
  if (isLoadingExperiencesSearch.value || hasErrorExperiencesSearch.value || hasNoResponseExperiencesSearch.value) return null;
  return [
    destinationTagsQuery.value,
    themeTagsQuery.value,
    preferenceTagsQuery.value,
    experienceEventStartAtDatesQuery.value,
    experienceEventStartTimeRangeFromQuery.value,
    experienceEventStartTimeRangeToQuery.value,
    selectedPageNum.value,
    selectedSortType.value,
  ].join('|');
});
if (import.meta.client) {
  // 通貨切替のように表示が同じまま再取得が走ると鍵が null を経由して同じ値へ戻るため、直前に送った鍵は送らない
  let sentExperienceListKey: string | null = null;
  watch(
    settledExperienceListKey,
    (key) => {
      if (key === null || key === sentExperienceListKey) return;
      sentExperienceListKey = key;
      sendGAWabunkaViewExperienceListEvent(selectedSortType.value, totalCount.value);
    },
    { immediate: true },
  );
}

const title = 'Wabunka Experiences';

// デスティネーションのみ指定されている場合を判定
const isDestinationPage = computed<boolean>(() => {
  const destinations = stringsFromQueryValue(destinationTagsQuery.value);
  const themes = stringsFromQueryValue(themeTagsQuery.value);
  const preferences = stringsFromQueryValue(preferenceTagsQuery.value);
  const timeOfDays = timeOfDayQuery.value;
  const hasDates = experienceEventStartAtDatesQuery.value !== '';
  const isFirstPage = selectedPageNum.value === 1;

  return destinations.length === 1 && themes.length === 0 && preferences.length === 0 && timeOfDays.length === 0 && !hasDates && isFirstPage && totalCount.value > 0;
});

// og:url に使うデスティネーション単体ページの URL
const destinationPageUrl = computed<string>(() => {
  if (!isDestinationPage.value) return '';
  const destinations = stringsFromQueryValue(destinationTagsQuery.value);
  return buildUrlWithEndSlash(`/${destinations[0]}`);
});

// DestinationThemeの組み合わせを判定
const isDestinationThemePage = computed<boolean>(() => {
  const destinations = stringsFromQueryValue(destinationTagsQuery.value);
  const themes = stringsFromQueryValue(themeTagsQuery.value);
  const preferences = stringsFromQueryValue(preferenceTagsQuery.value);
  const timeOfDays = timeOfDayQuery.value;
  const hasDates = experienceEventStartAtDatesQuery.value !== '';
  const isFirstPage = selectedPageNum.value === 1;

  return destinations.length === 1 && themes.length === 1 && preferences.length === 0 && timeOfDays.length === 0 && !hasDates && isFirstPage && totalCount.value > 0;
});

// og:url に使う DestinationTheme ページの URL
const destinationThemePageUrl = computed<string>(() => {
  if (!isDestinationThemePage.value) return '';
  const destinations = stringsFromQueryValue(destinationTagsQuery.value);
  const themes = stringsFromQueryValue(themeTagsQuery.value);
  return buildUrlWithEndSlash(`/${destinations[0]}/${themes[0]}`);
});

// Category (Japan Theme) page logic
const isThemePage = computed<boolean>(() => {
  const destinations = stringsFromQueryValue(destinationTagsQuery.value);
  const themes = stringsFromQueryValue(themeTagsQuery.value);
  const preferences = stringsFromQueryValue(preferenceTagsQuery.value);
  const timeOfDays = timeOfDayQuery.value;
  const hasDates = experienceEventStartAtDatesQuery.value !== '';
  const isFirstPage = selectedPageNum.value === 1;

  return destinations.length === 0 && themes.length === 1 && preferences.length === 0 && timeOfDays.length === 0 && !hasDates && isFirstPage && totalCount.value > 0;
});

const themePageUrl = computed<string>(() => {
  if (!isThemePage.value) return '';
  const themes = stringsFromQueryValue(themeTagsQuery.value);
  return buildUrlWithEndSlash(`/japan/${themes[0]}`);
});

// canonical は出力しない（2026-08-31 の要件確認で削除を決定 / JCAT-6193）。og:url の解決にのみ使う
const ogUrl = computed<string>(() => {
  return destinationPageUrl.value || destinationThemePageUrl.value || themePageUrl.value || meta.value?.metaCanonicalUrl || '';
});

useSeoMeta({
  title: () => meta.value?.pageTitle ?? title,
  description: () => meta.value?.metaDescription ?? '',
  ogTitle: () => meta.value?.pageTitle ?? title,
  ogDescription: () => meta.value?.metaDescription ?? '',
  // docs: https://github.com/harlan-zw/zhead/blob/main/packages/zhead/src/metaFlat.ts#L358
  ogUrl: () => ogUrl.value,
});
</script>
